import React, { useState } from "react";

import html2pdf from "html2pdf.js";
import ImageUploader from "react-images-upload";

import { gql } from "@apollo/client";
import northbeamHeaderText from "@assets/northbeam-header-text.svg";
import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { LoadingSlide } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import styled from "@emotion/styled";
import { useNorthbeamQuery } from "@hooks/http";
import { todayLocalAsISODate } from "@north-beam/nb-common";
import { PrimaryButton } from "@shared/buttons";
import { formatDateRange, FormattedDateRange } from "@shared/date-range-picker";
import { parseDateRangeArgument } from "@utils/index";

import { Overview } from "./overview";
import { useReportBodyState } from "./report-body-control";

function OverviewPagePrintMode(): JSX.Element {
  const { value: breakdownConfigs } = useSalesBreakdownConfigs();
  const { user } = useUser();
  const [title, setTitle] = useState<string>(
    user.companyName || "Northbeam Data Report",
  );
  const { state } = useReportBodyState(breakdownConfigs);
  const dateRange = parseDateRangeArgument(
    state.dateRange,
    todayLocalAsISODate(),
  );
  const exportAreaRef = React.useRef<HTMLDivElement>(null);
  const [isLoading, setLoading] = React.useState(true);
  const [fileName, setFileName] = React.useState(
    `${user.companyName} (${formatDateRange(dateRange)})`,
  );

  const exportAsPdf = React.useCallback(async () => {
    if (exportAreaRef.current) {
      html2pdf()
        .set({
          margin: [topMarginPx, marginPx, marginPx, marginPx],
          filename: `${fileName}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: {
            unit: "px",
            format: [pageWidthPx, Math.round(pageWidthPx * Math.SQRT2)],
            orientation: "portrait",
          },
        })
        .from(exportAreaRef.current)
        .save();
      // await exportToPdfRigged(exportAreaRef.current);
    }
  }, [exportAreaRef, fileName]);

  return (
    <>
      <div className="container my-3">
        <div className="row">
          <div className="col">
            <div className="card">
              <div className="card-header">PDF Export</div>
              <div className="card-body">
                <div className="row">
                  <div className="col">
                    <div>
                      <p>
                        Click the button below to export the report as a PDF.
                      </p>
                      <div className="mb-3">
                        File name:{" "}
                        <div className="input-group text-right">
                          <input
                            className="form-control"
                            value={fileName}
                            onChange={(e) => setFileName(e.target.value as any)}
                          />
                          <div className="input-group-append">
                            <div className="input-group-text">.pdf</div>
                          </div>
                        </div>
                      </div>
                      <PrimaryButton onClick={exportAsPdf} loading={isLoading}>
                        {isLoading ? "Report loading..." : "EXPORT"}
                      </PrimaryButton>
                    </div>
                    <div className="control-label mb-2 mt-5">
                      Set report title:
                    </div>
                    <input
                      type="text"
                      disabled={isLoading}
                      className="form-control"
                      value={isLoading ? "Report loading..." : title}
                      onChange={(event) => setTitle(event.target.value)}
                    />
                  </div>
                  <div className="col-2"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div style={reportWrapperBoundaryStyles}>
        <PrintStyle
          style={reportBoundaryStyles}
          ref={exportAreaRef}
          className="flex flex-col space-y-3"
        >
          <div className="flex justify-between items-start">
            <img
              className="h-6"
              src={northbeamHeaderText}
              alt="Northbeam text"
            />
            <div className="text-center">
              <h1 className="company-name pb-2">{title}</h1>
              <p>
                <FormattedDateRange dateRange={dateRange} />
              </p>
            </div>
            <div style={{ width: "200px", height: "54px" }}></div>
          </div>
          <Overview
            breakdownConfigs={breakdownConfigs}
            reportEventName="Request Overview Report Print View"
            onLoadingStatusChange={setLoading}
          />
        </PrintStyle>
      </div>
    </>
  );
}

const PrintStyle = styled.div`
  .company-name {
    font-size: 3.3em;
  }
  .nb-accounting-methods-read-more,
  .nb-accounting-methods-short-explanation,
  .nb-divider,
  .nb-breakdown-table,
  .nb-report-body-control,
  div[class$="-IndicatorsContainer"],
  i,
  button {
    display: none;
  }
  .nb-input,
  div[class$="-control"] {
    pointer-events: none;
    height: unset;
    border: none;

    & > *,
    & > * > * {
      overflow: visible !important;
      font-size: 1.1em !important;
      font-weight: 500 !important;
    }
  }
`;

const pageWidthPx = 1440;
const marginPx = 42;
const topMarginPx = 63;

const reportWrapperBoundaryStyles = {
  width: `${pageWidthPx}px`,
  minWidth: `${pageWidthPx}px`,
  maxWidth: `${pageWidthPx}px`,
  padding: `${marginPx}px`,
  paddingTop: `${topMarginPx}px`,
  margin: "0 auto",
} as const;

const reportBoundaryStyles = {
  width: `${pageWidthPx - 2 * marginPx}px`,
  minWidth: `${pageWidthPx - 2 * marginPx}px`,
  maxWidth: `${pageWidthPx - 2 * marginPx}px`,
} as const;

export default OverviewPagePrintMode;
